import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions
} from '@headlessui/react'
import { ChevronUpDownIcon } from '@heroicons/react/16/solid'
import { AnimatePresence, motion } from 'framer-motion'
import PropTypes from 'prop-types'
import { useState } from 'react'

import TalentComboboxOption from './TalentComboboxOption'

/**
 * Organism Component: TalentSearchCombobox
 * Searchable combobox for selecting talents
 */
const TalentSearchCombobox = ({
  talents,
  selectedTalents = [],
  onTalentAdd,
  placeholder = 'Name, E-Mail Adresse oder Kennung eingeben'
}) => {
  const [query, setQuery] = useState('')

  const filteredTalents =
    query === ''
      ? talents.filter(
          (talent) =>
            !selectedTalents.some((selected) => selected.id === talent.id)
        )
      : talents.filter(
          (talent) =>
            (talent.name || talent.email || String(talent.id))
              .toLowerCase()
              .includes(query.toLowerCase()) &&
            !selectedTalents.some((selected) => selected.id === talent.id)
        )

  const handleTalentSelect = (talent) => {
    onTalentAdd(talent)
    setQuery('')
  }

  return (
    <Combobox
      value={selectedTalents}
      onChange={() => {}}
      multiple
      immediate
      onClose={() => setQuery('')}
    >
      {({ open }) => (
        <div className='relative'>
          <ComboboxInput
            className='w-full rounded-md border border-gray-300 bg-white px-3 py-3 text-base placeholder-gray-500 focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue'
            placeholder={placeholder}
            onChange={(event) => setQuery(event.target.value)}
            displayValue={() => query}
          />
          <ComboboxButton className='absolute inset-y-0 right-0 flex items-center pr-3'>
            <ChevronUpDownIcon className='size-5 text-mediumGrey' />
          </ComboboxButton>
          <AnimatePresence>
            {open && (
              <ComboboxOptions
                as={motion.div}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                static
                onAnimationComplete={() => setQuery('')}
                className='absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg'
              >
                {filteredTalents.length === 0 && query !== '' ? (
                  <div className='px-4 py-3 text-sm text-gray-500'>
                    Keine Talente gefunden
                  </div>
                ) : (
                  filteredTalents.map((talent) => (
                    <ComboboxOption
                      key={talent.id}
                      value={talent}
                      disabled={!!talent.isLocked}
                      className={
                        talent.isLocked
                          ? 'cursor-default select-none'
                          : 'cursor-pointer select-none'
                      }
                    >
                      {({ focus }) => (
                        <TalentComboboxOption
                          talent={talent}
                          onAdd={handleTalentSelect}
                          focus={focus}
                        />
                      )}
                    </ComboboxOption>
                  ))
                )}
              </ComboboxOptions>
            )}
          </AnimatePresence>
        </div>
      )}
    </Combobox>
  )
}

TalentSearchCombobox.propTypes = {
  talents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      email: PropTypes.string,
      type: PropTypes.string,
      isLocked: PropTypes.bool
    })
  ).isRequired,
  selectedTalents: PropTypes.array,
  onTalentAdd: PropTypes.func.isRequired,
  placeholder: PropTypes.string
}

export default TalentSearchCombobox
